"use client";

import { useState } from "react";
import { X, User, Shield, Bell, Palette, PanelRight, Maximize2, Save, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { useAuth } from "@/contexts/auth-context";

interface UserSettingsDialogProps {
  open: boolean;
  onClose: () => void;
}

export function UserSettingsDialog({ open, onClose }: UserSettingsDialogProps) {
  const { user, tenant } = useAuth();
  const [activeTab, setActiveTab] = useState("profile");
  const [mode, setMode] = useState<"dialog" | "panel">("dialog");
  const [saved, setSaved] = useState(false);
  
  // Form state initialized from auth context
  const [formData, setFormData] = useState({
    displayName: user?.displayName || "管理员",
    email: user?.email || "",
    phone: user?.phone || "",
    department: "集团管理部",
    position: "超级管理员",
  });

  const handleSave = () => {
    // In real app, this would call an API to update user info
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };

  if (!open) return null;

  const tabs = [
    { id: "profile", label: "基本信息", icon: User },
    { id: "security", label: "安全设置", icon: Shield },
    { id: "notification", label: "通知偏好", icon: Bell },
    { id: "appearance", label: "外观设置", icon: Palette },
  ];

  const toggleMode = () => {
    setMode(mode === "dialog" ? "panel" : "dialog");
  };

  // Dialog mode: centered, 75% viewport
  // Panel mode: right side, 45% width
  const containerClass = mode === "dialog"
    ? "fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/30 backdrop-blur-sm"
    : "fixed inset-y-0 right-0 z-50 w-[45%] bg-background shadow-2xl border-l border-border";

  const contentClass = mode === "dialog"
    ? "w-[75vw] max-w-5xl h-[85vh] max-h-[800px] bg-background rounded-xl shadow-2xl flex flex-col overflow-hidden"
    : "w-full h-full bg-background flex flex-col";

  return (
    <div className={containerClass} onClick={onClose}>
      <div className={contentClass} onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-border/50 bg-gradient-to-r from-stellar-gold/5 to-transparent">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-stellar-gold/10 flex items-center justify-center">
              <User className="w-5 h-5 text-stellar-gold" />
            </div>
            <div>
              <h2 className="text-lg font-semibold">个人设置</h2>
              <p className="text-xs text-muted-foreground">管理您的账户信息和偏好</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {saved && (
              <span className="flex items-center gap-1 text-xs text-green-600">
                <Check className="w-3.5 h-3.5" /> 已保存
              </span>
            )}
            <button
              onClick={handleSave}
              className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
              title="保存设置"
            >
              <Save className="w-4 h-4 text-muted-foreground" />
            </button>
            <button
              onClick={toggleMode}
              className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
              title={mode === "dialog" ? "切换到侧边栏模式" : "切换到对话框模式"}
            >
              {mode === "dialog" ? (
                <PanelRight className="w-4 h-4 text-muted-foreground" />
              ) : (
                <Maximize2 className="w-4 h-4 text-muted-foreground" />
              )}
            </button>
            <button
              onClick={onClose}
              className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
            >
              <X className="w-5 h-5 text-muted-foreground" />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="flex flex-1 overflow-hidden">
          {/* Sidebar Tabs */}
          <div className="w-48 border-r border-border/50 bg-gray-50/30 p-3 space-y-1">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={cn(
                  "w-full flex items-center gap-3 px-4 py-3 rounded-lg text-sm transition-all",
                  activeTab === tab.id
                    ? "bg-stellar-gold/10 text-stellar-gold font-medium"
                    : "text-muted-foreground hover:bg-gray-100"
                )}
              >
                <tab.icon className="w-4 h-4" />
                {tab.label}
              </button>
            ))}
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-6">
            {activeTab === "profile" && (
              <div className="space-y-6">
                <div className="flex items-center gap-6">
                  <div className="relative">
                    <div className="w-20 h-20 rounded-full bg-gradient-to-br from-stellar-gold/20 to-stellar-gold/5 flex items-center justify-center text-2xl font-bold text-stellar-gold">
                      {(user?.displayName || "管").charAt(0)}
                    </div>
                    <button className="absolute bottom-0 right-0 w-7 h-7 rounded-full bg-stellar-gold text-white flex items-center justify-center shadow-lg hover:bg-stellar-gold/90 transition-colors">
                      <User className="w-3.5 h-3.5" />
                    </button>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold">{user?.displayName || "管理员"}</h3>
                    <p className="text-sm text-muted-foreground">{tenant?.modelType || "超级管理员"} · {tenant?.name || "系统管理"}</p>
                    <p className="text-xs text-muted-foreground mt-1">ID: {user?.id || "U001"}</p>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-foreground">姓名</label>
                    <input
                      type="text"
                      value={formData.displayName}
                      onChange={(e) => setFormData({ ...formData, displayName: e.target.value })}
                      className="w-full px-3 py-2 rounded-lg border border-border/50 bg-background text-sm focus:outline-none focus:ring-2 focus:ring-stellar-gold/30"
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-foreground">邮箱</label>
                    <input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="w-full px-3 py-2 rounded-lg border border-border/50 bg-background text-sm focus:outline-none focus:ring-2 focus:ring-stellar-gold/30"
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-foreground">手机号</label>
                    <input
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      className="w-full px-3 py-2 rounded-lg border border-border/50 bg-background text-sm focus:outline-none focus:ring-2 focus:ring-stellar-gold/30"
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-foreground">所属部门</label>
                    <input
                      type="text"
                      value={formData.department}
                      onChange={(e) => setFormData({ ...formData, department: e.target.value })}
                      className="w-full px-3 py-2 rounded-lg border border-border/50 bg-background text-sm focus:outline-none focus:ring-2 focus:ring-stellar-gold/30"
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-foreground">职位</label>
                    <input
                      type="text"
                      value={formData.position}
                      onChange={(e) => setFormData({ ...formData, position: e.target.value })}
                      className="w-full px-3 py-2 rounded-lg border border-border/50 bg-background text-sm focus:outline-none focus:ring-2 focus:ring-stellar-gold/30"
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-foreground">所属租户</label>
                    <input
                      type="text"
                      value={tenant?.name || "系统管理"}
                      disabled
                      className="w-full px-3 py-2 rounded-lg border border-border/50 bg-gray-50 text-sm text-muted-foreground cursor-not-allowed"
                    />
                  </div>
                </div>
              </div>
            )}

            {activeTab === "security" && (
              <div className="space-y-6">
                <div className="space-y-3">
                  <h3 className="text-sm font-medium text-foreground flex items-center gap-2">
                    <Shield className="w-4 h-4 text-stellar-gold" /> 修改密码
                  </h3>
                  <div className="space-y-3 p-4 rounded-lg border border-border/50 bg-gray-50/50">
                    <input
                      type="password"
                      placeholder="当前密码"
                      className="w-full px-3 py-2 rounded-lg border border-border/50 bg-background text-sm focus:outline-none focus:ring-2 focus:ring-stellar-gold/30"
                    />
                    <input
                      type="password"
                      placeholder="新密码"
                      className="w-full px-3 py-2 rounded-lg border border-border/50 bg-background text-sm focus:outline-none focus:ring-2 focus:ring-stellar-gold/30"
                    />
                    <input
                      type="password"
                      placeholder="确认新密码"
                      className="w-full px-3 py-2 rounded-lg border border-border/50 bg-background text-sm focus:outline-none focus:ring-2 focus:ring-stellar-gold/30"
                    />
                    <Button size="sm" className="bg-stellar-gold hover:bg-stellar-gold/90 text-white">
                      更新密码
                    </Button>
                  </div>
                </div>

                <div className="space-y-3">
                  <h3 className="text-sm font-medium text-foreground flex items-center gap-2">
                    <Shield className="w-4 h-4 text-stellar-gold" /> 登录设备
                  </h3>
                  <div className="space-y-2">
                    {[
                      { device: "Chrome / macOS", location: "北京", time: "当前设备", current: true },
                      { device: "Safari / iPhone", location: "上海", time: "2小时前", current: false },
                    ].map((item, i) => (
                      <div key={i} className="flex items-center justify-between p-3 rounded-lg border border-border/50 bg-gray-50/50">
                        <div>
                          <p className="text-sm font-medium">{item.device}</p>
                          <p className="text-xs text-muted-foreground">{item.location} · {item.time}</p>
                        </div>
                        {item.current ? (
                          <span className="text-xs px-2 py-1 rounded-full bg-green-50 text-green-600 border border-green-200">当前</span>
                        ) : (
                          <button className="text-xs text-red-500 hover:text-red-600">移除</button>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {activeTab === "notification" && (
              <div className="space-y-4">
                {[
                  { title: "系统通知", desc: "系统升级、维护通知", enabled: true },
                  { title: "审批通知", desc: "待审批事项提醒", enabled: true },
                  { title: "告警通知", desc: "安全告警、异常告警", enabled: true },
                  { title: "租户通知", desc: "租户相关变更通知", enabled: false },
                  { title: "邮件通知", desc: "重要事项邮件提醒", enabled: true },
                  { title: "短信通知", desc: "紧急事项短信提醒", enabled: false },
                ].map((item, i) => (
                  <div key={i} className="flex items-center justify-between p-3 rounded-lg border border-border/50">
                    <div>
                      <p className="text-sm font-medium">{item.title}</p>
                      <p className="text-xs text-muted-foreground">{item.desc}</p>
                    </div>
                    <button
                      className={cn(
                        "w-10 h-5 rounded-full transition-colors relative",
                        item.enabled ? "bg-stellar-gold" : "bg-gray-200"
                      )}
                    >
                      <div className={cn(
                        "absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform",
                        item.enabled ? "translate-x-5" : "translate-x-0.5"
                      )} />
                    </button>
                  </div>
                ))}
              </div>
            )}

            {activeTab === "appearance" && (
              <div className="space-y-5">
                <div className="space-y-3">
                  <h3 className="text-sm font-medium text-foreground">主题模式</h3>
                  <div className="grid grid-cols-3 gap-3">
                    {[
                      { id: "light", label: "明亮", active: true },
                      { id: "dark", label: "暗黑", active: false },
                      { id: "auto", label: "跟随系统", active: false },
                    ].map((theme) => (
                      <button
                        key={theme.id}
                        className={cn(
                          "p-4 rounded-lg border-2 transition-all text-center",
                          theme.active
                            ? "border-stellar-gold bg-stellar-gold/5"
                            : "border-border/50 hover:border-stellar-gold/30"
                        )}
                      >
                        <div className={cn(
                          "w-full h-8 rounded mb-2",
                          theme.id === "light" ? "bg-white border border-border" :
                          theme.id === "dark" ? "bg-gray-800" : "bg-gradient-to-r from-white to-gray-800"
                        )} />
                        <span className="text-xs font-medium">{theme.label}</span>
                      </button>
                    ))}
                  </div>
                </div>

                <div className="space-y-3">
                  <h3 className="text-sm font-medium text-foreground">主题色</h3>
                  <div className="flex gap-3">
                    {[
                      { color: "#b8943f", name: "星辰金", active: true },
                      { color: "#7c3aed", name: "极光紫", active: false },
                      { color: "#2563eb", name: "深海蓝", active: false },
                      { color: "#059669", name: "翡翠绿", active: false },
                      { color: "#dc2626", name: "火星红", active: false },
                    ].map((c) => (
                      <button
                        key={c.color}
                        className={cn(
                          "flex flex-col items-center gap-1.5 p-2 rounded-lg transition-all",
                          c.active ? "bg-gray-100" : "hover:bg-gray-50"
                        )}
                      >
                        <div
                          className={cn(
                            "w-8 h-8 rounded-full border-2",
                            c.active ? "border-foreground" : "border-transparent"
                          )}
                          style={{ backgroundColor: c.color }}
                        />
                        <span className="text-[10px] text-muted-foreground">{c.name}</span>
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 px-6 py-4 border-t border-border/50 bg-gray-50/50">
          <Button variant="outline" size="sm" onClick={onClose}>
            取消
          </Button>
          <Button size="sm" className="bg-stellar-gold hover:bg-stellar-gold/90 text-white" onClick={onClose}>
            保存设置
          </Button>
        </div>
      </div>
    </div>
  );
}
